<template>
  <div class="service-plan-history">
    <a-divider>История изменений</a-divider>

    <div
      v-if="!loading && hasAnyLogs"
      class="category-filter-buttons">
      <div class="category-buttons-container">
        <div class="filters-header">
          <span class="filters-title">Фильтры:</span>
          <div class="filter-controls">
            <a-button
              size="small"
              class="control-button"
              @click="resetAllFilters">
              Сбросить все
            </a-button>
            <a-button
              size="small"
              class="control-button"
              @click="selectAllFilters">
              Выбрать все
            </a-button>
          </div>
        </div>
        <div class="category-buttons-scroll">
          <div
            v-for="category in allCategories"
            :key="category"
            class="category-button-wrapper">
            <a-button
              :type="isCategorySelected(category) ? 'primary' : 'default'"
              size="small"
              class="category-button"
              :title="actionCategories[category]?.description"
              @click="toggleCategory(category)">
              <template #icon>
                <component :is="getCategoryIcon(category)" />
              </template>
              <span
                v-if="isCategorySelected(category)"
                class="category-count">
                {{ getCategoryCount(category) }}
              </span>
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <a-skeleton
      v-if="loading"
      active
      :paragraph="{ rows: 7 }" />

    <a-empty
      v-if="!loading && activityLogs.length === 0 && !hasAnyLogs"
      :image="simpleImage"
      description="Нет записей в истории изменений" />

    <a-empty
      v-if="!loading && activityLogs.length === 0 && hasAnyLogs"
      :image="simpleImage"
      :description="`Нет записей по выбранным категориям`" />

    <a-timeline v-if="!loading && activityLogs.length">
      <a-timeline-item
        v-for="(log) in activityLogs"
        :key="log.id"
        :color="getTimelineColor(log.action_category)">
        <div class="log-header">
          <a-row
            flex
            :style="{ flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between' }">
            <div class="category-badge-container">
              <div class="category-badge-wrapper">
                <a-tag
                  :color="getActionCategoryColor(log.action_category)"
                  :style="{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: 0,
                    borderRadius: '4px'
                  }">
                  <component
                    :is="getCategoryIcon(log.action_category)"
                    :style="{ fontSize: '12px' }" />
                  {{ getActionCategoryLabel(log.action_category) }}
                </a-tag>

                <a-tag
                  v-if="props.crmPlanId && log.plan_type"
                  :style="{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    margin: '0 8px',
                    borderRadius: '4px',
                    backgroundColor: getPlanTypeColor(log.plan_type),
                    color: 'white',
                    border: 'none'
                  }"
                  @click="handlePlanTypeClick(log.plan_type, log.plan_id)">
                  <component
                    :is="getPlanTypeIcon(log.plan_type)"
                    :style="{ fontSize: '12px' }" />
                  {{ getPlanTypeLabel(log.plan_type) }}
                </a-tag>
              </div>
            </div>

            <span class="log-timestamp">
              <clock-circle-outlined :style="{ marginRight: '4px' }" />
              {{ formatDate(log.created_at) }}
            </span>
          </a-row>
        </div>

        <div class="message-user-row">
          <div
            v-if="log.message"
            class="message-container">
            <span class="field-value message-content">{{ log.message }}</span>
          </div>

          <div class="user-container">
            <div class="user-info">
              <user-outlined />
              <span>{{ getUserInfo(log?.user) }}</span>
            </div>
          </div>
        </div>
      </a-timeline-item>
    </a-timeline>
  </div>
</template>

<script setup>
import {
  computed, onMounted, ref,
} from 'vue';
import { useStore } from 'vuex';
import { Empty, message } from 'ant-design-vue';
import {
  UserOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  UserSwitchOutlined,
  DatabaseOutlined,
  PictureOutlined,
  SettingOutlined,
  CopyOutlined,
  HomeOutlined,
  BuildOutlined,
  ProjectOutlined,
} from '@ant-design/icons-vue';
import moment from 'moment';
import { PlanActivityLogType } from '@/enums/planActivityLogType';
import { useRouter } from 'vue-router';

const props = defineProps({
  planId: {
    type: String,
    default: null,
  },
  crmPlanId: {
    type: Number,
    default: null,
  },
  planType: {
    type: PlanActivityLogType,
    default: PlanActivityLogType.CrmPlan3d,
  },
});

const store = useStore();
const router = useRouter();
const loading = ref(false);
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

const allActivityLogs = ref([]);

const allCategories = [
  'initialization',
  'status',
  'json',
  'responsible',
  'metadata',
  'renders',
  'render_parameters',
  'copy',
];

const selectedCategories = ref([...allCategories]);

const actionCategories = {
  initialization: {
    label: 'Инициализация',
    color: 'blue',
    icon: PlayCircleOutlined,
    description: 'Создание и начальная настройка плана',
  },
  status: {
    label: 'Статус',
    color: 'green',
    icon: CheckCircleOutlined,
    description: 'Изменения статусов плана',
  },
  json: {
    label: 'JSON',
    color: 'geekblue',
    icon: CodeOutlined,
    description: 'Изменения JSON данных',
  },
  responsible: {
    label: 'Ответственные',
    color: 'orange',
    icon: UserSwitchOutlined,
    description: 'Смена ответственных лиц',
  },
  metadata: {
    label: 'Метаданные',
    color: 'purple',
    icon: DatabaseOutlined,
    description: 'Изменения метаданных',
  },
  renders: {
    label: 'Рендеры',
    color: 'magenta',
    icon: PictureOutlined,
    description: 'Операции с рендерами',
  },
  render_parameters: {
    label: 'Параметры рендера',
    color: 'cyan',
    icon: SettingOutlined,
    description: 'Настройки рендеринга',
  },
  copy: {
    label: 'Копирование',
    color: 'gold',
    icon: CopyOutlined,
    description: 'Операции копирования',
  },
};

const planTypes = {
  [PlanActivityLogType.CrmPlan3d]: {
    label: '3D',
    color: '#1890ff',
    icon: HomeOutlined,
    description: 'Трехмерный план помещения',
    route: (planId) => `/crm/plans/3d/${planId}`,
  },
  [PlanActivityLogType.CrmPlan2d]: {
    label: '2D',
    color: '#52c41a',
    icon: ProjectOutlined,
    description: 'Двухмерный план помещения',
    route: (planId) => `/crm/plans/${planId}`,
  },
  [PlanActivityLogType.ServicePlan]: {
    label: 'Сервисная',
    color: '#fa8c16',
    icon: BuildOutlined,
    description: 'Сервисный план',
    route: (planId) => `/plans/${planId}`,
  },
};

const activityLogs = computed(() => (props.crmPlanId
  ? store.state.crm.plan.activityLogs
  : store.state.plans.current.activityLogs));

const hasAnyLogs = computed(() => allActivityLogs.value.length > 0);

const formatDate = (dateString) => moment(dateString).format('DD.MM.YYYY HH:mm:ss');

const getActionCategoryLabel = (category) => actionCategories[category]?.label || actionCategories.renders.label;

const getActionCategoryColor = (category) => actionCategories[category]?.color || actionCategories.renders.color;

const getCategoryIcon = (category) => actionCategories[category]?.icon || PictureOutlined;

const getPlanTypeLabel = (planType) => planTypes[planType]?.label || planTypes[PlanActivityLogType.CrmPlan3d].label;

const getPlanTypeColor = (planType) => planTypes[planType]?.color || planTypes[PlanActivityLogType.CrmPlan3d].color;

const getPlanTypeIcon = (planType) => planTypes[planType]?.icon || HomeOutlined;

const getTimelineColor = (category) => {
  const colors = {
    initialization: 'blue',
    status: 'green',
    json: 'geekblue',
    responsible: 'orange',
    metadata: 'purple',
    renders: 'magenta',
    render_parameters: 'cyan',
    copy: 'gold',
  };
  return colors[category] || 'magenta';
};

const loadServicePlanActivityLogs = async () => {
  loading.value = true;
  try {
    await store.dispatch('plans/getServicePlanActivityLogs', {
      id: props.planId,
      actionCategory: selectedCategories.value.length > 0 ? selectedCategories.value : null,
    });

    if (selectedCategories.value.length === allCategories.length) {
      allActivityLogs.value = [...store.state.plans.current.activityLogs];
    }
  } catch (error) {
    console.error('Ошибка загрузки истории изменений:', error);
    message.error('Не удалось загрузить историю изменений');
  } finally {
    loading.value = false;
  }
};

const loadCrmPlanActivityLogs = async () => {
  loading.value = true;
  try {
    await store.dispatch('crm/plan/getCrmPlanActivityLogs', {
      id: props.crmPlanId,
      planType: props.planType,
      actionCategory: selectedCategories.value.length > 0 ? selectedCategories.value : null,
    });

    if (selectedCategories.value.length === allCategories.length) {
      allActivityLogs.value = [...store.state.crm.plan.activityLogs];
    }
  } catch (error) {
    console.error('Ошибка загрузки истории изменений:', error);
    message.error('Не удалось загрузить историю изменений');
  } finally {
    loading.value = false;
  }
};

const loadActivityLogs = async () => {
  if (props.crmPlanId) {
    await loadCrmPlanActivityLogs();
  } else {
    await loadServicePlanActivityLogs();
  }
};

const isCategorySelected = (category) => selectedCategories.value.includes(category);

const toggleCategory = (category) => {
  const index = selectedCategories.value.indexOf(category);
  if (index > -1) {
    selectedCategories.value.splice(index, 1);
  } else {
    selectedCategories.value.push(category);
  }

  if (selectedCategories.value.length === 0) {
    selectedCategories.value = [...allCategories];
  }

  loadActivityLogs();
};

const getCategoryCount = (category) => allActivityLogs.value.filter((log) => log.action_category === category).length;

const resetAllFilters = () => {
  selectedCategories.value = [];
  loadActivityLogs();
};

const selectAllFilters = () => {
  selectedCategories.value = [...allCategories];
  loadActivityLogs();
};

const handlePlanTypeClick = (planType, planId) => {
  const planTypeConfig = planTypes[planType];
  if (!planTypeConfig || !planTypeConfig.route) {
    message.warning('Неизвестный тип плана');
    return;
  }

  const route = planTypeConfig.route(planId);

  const currentPath = router.currentRoute.value.path;
  if (currentPath === route) {
    return;
  }

  router.push(route);
};

const getUserInfo = (userData) => {
  if (userData?.id && userData?.name) {
    return `${userData?.id}: ${userData?.name}`;
  }
  return 'Неавторизованный';
};

onMounted(() => {
  loadActivityLogs();
});

defineExpose({
  loadActivityLogs,
  loading,
  activityLogs,
});
</script>

<style scoped>
.service-plan-history {
  height: 100%;
  overflow-y: auto;
}

.category-filter-buttons {
  margin-bottom: 20px;
}

.category-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.filters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  margin-bottom: 8px;
}

.filters-title {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
}

.filter-controls {
  display: flex;
  gap: 8px;
}

.control-button {
  font-size: 12px;
  padding: 0 8px;
  height: 24px;
}

.category-buttons-scroll {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
}

.category-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.category-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.category-button.ant-btn-primary {
  border-color: transparent;
  font-weight: 500;
}

.category-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-size: 11px;
  padding: 0 4px;
  margin-left: 4px;
  flex-shrink: 0;
}

.category-button-wrapper {
  display: inline-flex;
  align-items: center;
}

.category-badge-container {
  display: flex;
  align-items: center;
  min-width: 0;
}

.category-badge-wrapper {
  display: flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
}

.log-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.log-timestamp {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.log-body {
  padding-left: 8px;
}

.message-user-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.message-container {
  flex: 1;
  min-width: 0;
}

.user-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 120px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  white-space: nowrap;
}

.field-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  min-width: 80px;
  margin-right: 8px;
  flex-shrink: 0;
}

.field-value {
  color: rgba(0, 0, 0, 0.65);
  flex: 1;
  word-break: break-word;
}

.message-content {
  background-color: #fafafa;
  padding: 4px 8px;
  border-radius: 4px;
  border-left: 3px solid #1890ff;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
}

.user-info, .log-id {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.45);
}

.user-info span {
  color: rgba(0, 0, 0, 0.65);
}

.ant-timeline-item {
  padding-bottom: 20px;
}

.ant-timeline-item:last-child {
  padding-bottom: 0;
}

.ant-timeline-item-tail {
  left: 6px;
}

.ant-timeline-item-head {
  width: 12px;
  height: 12px;
}

/* Стили для скролла кнопок */
.category-buttons-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.category-buttons-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.category-buttons-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.category-buttons-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

@media (max-width: 768px) {
  .filters-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .filter-controls {
    width: 100%;
    justify-content: flex-end;
  }

  .message-user-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .user-container {
    justify-content: flex-start;
    min-width: auto;
  }

  .log-header {
    flex-wrap: wrap;
  }

  .category-badge-wrapper {
    flex-wrap: wrap;
  }
}
</style>
